import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Anchor, ChevronRight, Wifi, Truck } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import { API_BASE_URL } from '../config/apiMode';

const fmt = (n) => new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',minimumFractionDigits:0}).format(n||0);

export default function CapexConfigurator() {
  const { calculateCapex, saveCapex, setCapexConfig, listCapex, token, user } = useApp();
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isPartner = user?.role === 'partner' || user?.role === 'admin';

  const [model, setModel] = useState('FB3');
  const [fb3Package, setFb3Package] = useState('fb3_base');
  const [numDrones, setNumDrones] = useState(1);
  const [numBattery, setNumBattery] = useState(0);
  const [numChargers, setNumChargers] = useState(0);
  const [controllerType, setControllerType] = useState('handheld');
  const [authorizationSupportLevel, setAuthorizationSupportLevel] = useState('vlos');
  const [mmsPackage, setMmsPackage] = useState('mms_3m');
  const [supportWarrantyPackage, setSupportWarrantyPackage] = useState('standard_12m');
  const [starlink, setStarlink] = useState(false);
  const [trailer, setTrailer] = useState(false);
  const [bottomHook, setBottomHook] = useState(false);
  const [trainingA2, setTrainingA2] = useState(false);
  const [trainingSts, setTrainingSts] = useState(false);
  const [trainingCommCrm, setTrainingCommCrm] = useState(false);
  const [trainingSora, setTrainingSora] = useState(false);
  const [trainingPiloting, setTrainingPiloting] = useState(false);
  const [trainingMaintenance, setTrainingMaintenance] = useState(false);
  const [capexResult, setCapexResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [partnerClients, setPartnerClients] = useState([]);

  useEffect(() => {
    const editId = location.state?.editId;
    if (!editId) return;
    loadCapexById(editId);
  }, [location.state]);

  useEffect(() => {
    if (!token || !isPartner) return;
    axios.create({ baseURL: API_BASE_URL, headers: { Authorization: `Bearer ${token}` } })
      .get('/api/partner/clients')
      .then(r => setPartnerClients(r.data || []))
      .catch(() => {});
  }, [token, isPartner]);

  useEffect(() => {
    const prefill = location.state?.prefillCapex;
    if (!prefill) return;
    setModel(prefill.droneModel || 'FB3');
    setFb3Package(prefill.fb3Package || (prefill.droneModel === 'FB4' ? 'fb3_max_ultra' : 'fb3_base'));
    setNumDrones(prefill.numDrones || 1);
    setNumBattery(prefill.numBattery || 0);
    setNumChargers(prefill.numChargers || 0);
    setControllerType(prefill.controllerType || (prefill.droneModel === 'FB4' ? 'bvlos' : 'handheld'));
    setAuthorizationSupportLevel(prefill.authorizationSupportLevel || (prefill.droneModel === 'FB4' ? 'bvlos' : 'vlos'));
    setMmsPackage(prefill.mmsPackage || (prefill.droneModel === 'FB4' ? 'mms_24m' : 'mms_3m'));
    setSupportWarrantyPackage(prefill.supportWarrantyPackage || (prefill.droneModel === 'FB4' ? 'premium_24m' : 'standard_12m'));
    recalculate(prefill);
  }, [location.state?.prefillCapex]);

  const loadCapexById = async (id) => {
    try {
      const list = await listCapex();
      const config = (list || []).find(c => c.id === id);
      if (!config) return;
      setModel(config.drone_model || 'FB3');
      setFb3Package(config.package_tier || 'fb3_base');
      setNumDrones(config.num_drones || 1);
      setNumBattery(config.num_battery || 0);
      setNumChargers(config.num_chargers || 0);
      setControllerType(config.controller_type || 'handheld');
      setAuthorizationSupportLevel(config.authorization_support || 'vlos');
      setMmsPackage(config.connectivity_package || 'mms_3m');
      setSupportWarrantyPackage(config.support_warranty || 'standard_12m');
      setStarlink(!!config.has_starlink);
      setTrailer(!!config.has_trailer);
      setBottomHook(!!config.has_bottom_hook);
      setTrainingA2(!!config.training_a2);
      setTrainingSts(!!config.training_sts);
      setTrainingCommCrm(!!config.training_comm_crm);
      setTrainingSora(!!config.training_sora);
      setTrainingPiloting(!!config.training_piloting);
      setTrainingMaintenance(!!config.training_maintenance);
      const quote = await calculateCapex({
        droneModel: config.drone_model || 'FB3',
        fb3Package: config.package_tier || 'fb3_base',
        numDrones: config.num_drones || 1,
        numBattery: config.num_battery || 0,
        numChargers: config.num_chargers || 0,
        controllerType: config.controller_type || 'handheld',
        authorizationSupportLevel: config.authorization_support || 'vlos',
        mmsPackage: config.connectivity_package || 'mms_3m',
        supportWarrantyPackage: config.support_warranty || 'standard_12m',
        hasWinch: !!config.has_winch,
        hasLidar: !!config.has_lidar,
        hasStarlink: !!config.has_starlink,
        hasTrailer: !!config.has_trailer,
        hasBottomHook: !!config.has_bottom_hook,
        trainingA2: !!config.training_a2,
        trainingSts: !!config.training_sts,
        trainingCommCrm: !!config.training_comm_crm,
        trainingSora: !!config.training_sora,
        trainingPiloting: !!config.training_piloting,
        trainingMaintenance: !!config.training_maintenance
      });
      setCapexResult(quote);
      setViewMode(true);
    } catch(e) { console.error(e); }
  };

  const buildParams = (overrides = {}) => ({
    droneModel: model, fb3Package, numDrones, numBattery, numChargers, controllerType,
    authorizationSupportLevel, mmsPackage, supportWarrantyPackage,
    hasStarlink: starlink, hasTrailer: trailer, hasBottomHook: bottomHook,
    trainingA2, trainingSts, trainingCommCrm, trainingSora, trainingPiloting, trainingMaintenance,
    ...overrides
  });

  const recalculate = async (overrides = {}) => {
    setViewMode(false);
    const r = await calculateCapex(buildParams(overrides));
    setCapexResult(r);
    return r;
  };

  const handleSave = async () => {
    if (!capexResult) return;
    setSaving(true);
    try {
      const selectedCustomer = partnerClients.find(c => c.id === parseInt(customerId));
      const customerLabel = selectedCustomer ? ` — ${selectedCustomer.name}` : '';
      const saved = await saveCapex({
        name: `Config ${model} x${numDrones}${customerLabel}`,
        ...buildParams(),
        totalCapex: capexResult.totalCapex,
        leasingMonthly: capexResult.leasingMonthly,
        customerId: customerId || null
      });
      setCapexConfig(saved);
      alert(t('capex.savedAlert'));
    } finally { setSaving(false); }
  };

  const droneData = {
    FB3: { name:'FB3', sub:t('capex.drones.fb3Sub'), payload:'100kg Payload', payloadKg: 100, flight:'9-28 min', img:'/images/drones/fb3.jpg', infosheet:'/images/drones/fb3-infosheet.pdf' },
    FB4: { name:'FB4', sub:t('capex.drones.fb4Sub'), payload:'200kg Payload', payloadKg: 200, flight:'9-53 min', img:'/images/drones/fb4.jpg', infosheet:'/images/drones/fb4-infosheet.pdf' },
  };

  const fb3Packages = [
    { key: 'fb3_base', label: 'FB3 Essential', price: '€ 45.000', sub: 'Handheld 7" (RF+4G), 1 stream, VLOS, MMS 3 months, standard support' },
    { key: 'fb3_pro', label: 'FB3 Standard', price: '€ 66.900', sub: 'Handheld 10", BVLOS support, RF containment, MMS 12 months, premium support' },
    { key: 'fb3_max_ultra', label: 'FB3 Pro', price: '€ 76.900', sub: 'Mobile BVLOS GCS, 2 streams, 4G+RF containment, MMS 24 months, premium support' },
  ];
  const controllerPrices = { handheld: 0, handheld_pro: 6000, bvlos: 13000 };
  const controllers = [
    { val:'handheld', label:'Base Handheld', sub:'RF + 4G — 7" screen' },
    { val:'handheld_pro', label:'Handheld PRO', sub:'RF + 4G — 10" screen' },
    { val:'bvlos', label:'BVLOS Ground Station', sub:'Quad redundant datalink + 2× 4G + 3 screens' },
  ];

  useEffect(() => {
    if (model === 'FB4') {
      setFb3Package('fb3_max_ultra');
      setControllerType('bvlos');
      setAuthorizationSupportLevel('bvlos');
      setMmsPackage('mms_24m');
      setSupportWarrantyPackage('premium_24m');
      recalculate({
        droneModel: 'FB4',
        fb3Package: 'fb3_max_ultra',
        controllerType: 'bvlos',
        authorizationSupportLevel: 'bvlos',
        mmsPackage: 'mms_24m',
        supportWarrantyPackage: 'premium_24m'
      });
    }
  }, [model]);

  const packageDefaultController = model === 'FB4'
    ? 'bvlos'
    : (fb3Package === 'fb3_pro' ? 'handheld_pro' : fb3Package === 'fb3_max_ultra' ? 'bvlos' : 'handheld');

  const accessories = [
    { key:'starlink',   label:'Starlink Kit',       sub:t('capex.accessories.starlink'),   price:'€ 7.900', icon:Wifi,   state:starlink,   toggle:() => { const v=!starlink;   setStarlink(v);   recalculate({hasStarlink:v}); }},
    { key:'trailer',    label:'Trailer',            sub:t('capex.accessories.trailer'),    price:'€ 6.900', icon:Truck,  state:trailer,    toggle:() => { const v=!trailer;    setTrailer(v);    recalculate({hasTrailer:v}); }},
    { key:'bottomHook', label:'Bottom Remote Hook', sub:t('capex.accessories.bottomHook'), price:'€ 3.900', icon:Anchor, state:bottomHook, toggle:() => { const v=!bottomHook; setBottomHook(v); recalculate({hasBottomHook:v}); }},
  ];

  const trainings = [
    { key:'a2',          label:'A2 Certificate',          sub:t('capex.training.a2Sub'),          price:'€ 500',   state:trainingA2,          set:setTrainingA2,          field:'trainingA2' },
    { key:'sts',         label:'STS Scenario Training',   sub:t('capex.training.stsSub'),         price:'€ 200',   state:trainingSts,         set:setTrainingSts,         field:'trainingSts' },
    { key:'commCrm',     label:'Aeronautical Comm + CRM', sub:t('capex.training.commCrmSub'),     price:'€ 500',   state:trainingCommCrm,     set:setTrainingCommCrm,     field:'trainingCommCrm' },
    { key:'sora',        label:'SORA Introduction',       sub:t('capex.training.soraSub'),        price:'€ 500',   state:trainingSora,        set:setTrainingSora,        field:'trainingSora' },
    { key:'piloting',    label:'FB Piloting Training',    sub:t('capex.training.pilotingSub'),    price:'€ 2.900', state:trainingPiloting,    set:setTrainingPiloting,    field:'trainingPiloting' },
    { key:'maintenance', label:'FB Maintenance Training', sub:t('capex.training.maintenanceSub'), price:'€ 2.100', state:trainingMaintenance, set:setTrainingMaintenance, field:'trainingMaintenance' },
  ];

  return (
    <div className="page-content" style={{ maxWidth:1200 }}>
      <div style={{ marginBottom:'2rem', display:'flex', flexDirection:isMobile ? 'column' : 'row', gap:isMobile ? '1rem' : 0, justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-head)', fontSize:'1.75rem', fontWeight:800, color:'var(--primary)' }}>
            {t('capex.title')}
          </h1>
          <p style={{ color:'var(--on-surface-muted)', marginTop:'0.25rem' }}>{t('capex.subtitle')}</p>
        </div>
        {viewMode && (
          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.5rem 1rem', background:'#fef9c3', borderRadius:8, fontSize:'0.8rem', fontWeight:600, color:'#854d0e' }}>
            👁 {t('capex.viewMode')}
          </div>
        )}
      </div>

      {/* CUSTOMER SELECTOR — visibile solo per utenti partner/admin */}
      {isPartner && (
        <div style={{ background: 'linear-gradient(135deg, #eff6ff, #f0fdf4)', border: '1px solid #bfdbfe', borderRadius: 10, padding: '0.9rem 1.1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, color: '#1e3a8a', fontSize: '0.87rem', whiteSpace: 'nowrap' }}>
            👤 {t('partner.capex.customer')}
          </div>
          <select
            value={customerId || ''}
            onChange={e => setCustomerId(e.target.value || null)}
            style={{ flex: 1, minWidth: 200, padding: '0.45rem 0.65rem', borderRadius: 8, border: '1px solid #bfdbfe', fontSize: '0.87rem', background: 'white', color: '#0f172a' }}
          >
            <option value="">{t('partner.capex.customerPlaceholder')}</option>
            {partnerClients.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}{c.company ? ` — ${c.company}` : ''}
              </option>
            ))}
          </select>
          {customerId && (
            <button
              onClick={() => setCustomerId(null)}
              style={{ padding: '0.35rem 0.65rem', borderRadius: 7, border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem' }}
            >
              ✕
            </button>
          )}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:isMobile ? '1fr' : '1fr 340px', gap:'2rem' }}>
        <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>

          {/* STEP 01 */}
          <div className="card">
            <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--teal)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>STEP 01 OF 05</div>
            <h2 style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.5rem', color:'var(--primary)', marginBottom:'0.375rem' }}>{t('capex.step1.title')}</h2>
            <p style={{ color:'var(--on-surface-muted)', fontSize:'0.875rem', marginBottom:'1.5rem' }}>{t('capex.step1.desc')}</p>
            <div style={{ display:'grid', gridTemplateColumns:isMobile ? '1fr' : '1fr 1fr', gap:'1rem' }}>
              {Object.entries(droneData).map(([k,d]) => (
                <div key={k} onClick={() => {
                  if (k === 'FB3') {
                    setModel('FB3');
                    setFb3Package('fb3_base');
                    setControllerType('handheld');
                    setAuthorizationSupportLevel('vlos');
                    setMmsPackage('mms_3m');
                    setSupportWarrantyPackage('standard_12m');
                    recalculate({
                      droneModel: 'FB3',
                      fb3Package: 'fb3_base',
                      controllerType: 'handheld',
                      authorizationSupportLevel: 'vlos',
                      mmsPackage: 'mms_3m',
                      supportWarrantyPackage: 'standard_12m'
                    });
                  } else {
                    setModel('FB4');
                  }
                }}
                  style={{ border:`2px solid ${model===k?'var(--primary)':'var(--surface-high)'}`, borderRadius:'var(--radius-lg)', padding:'1.25rem', cursor:'pointer', background:model===k?'rgba(0,58,67,0.04)':'white', transition:'all 0.2s', position:'relative' }}>
                  {model===k && (
                    <div style={{ position:'absolute', top:12, right:12, width:22, height:22, background:'var(--primary)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Check size={12} color="white" />
                    </div>
                  )}
                  <div style={{ width:'100%', height:120, borderRadius:8, overflow:'hidden', marginBottom:'0.75rem', background:'var(--surface-low)' }}>
                    <img src={d.img} alt={d.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.style.display='none'; }} />
                  </div>
                  <div style={{ fontFamily:'var(--font-head)', fontWeight:700, color:'var(--primary)' }}>{d.name}</div>
                  <div style={{ fontSize:'0.75rem', color:'var(--on-surface-muted)', marginBottom:'0.75rem' }}>{d.sub}</div>
                  <div style={{ fontSize:'0.8rem', color:'var(--on-surface)' }}>{d.payload}</div>
                  <div style={{ fontSize:'0.75rem', color:'var(--on-surface-muted)', marginTop:'0.1rem' }}>
                    {(d.payloadKg * 2.20462).toFixed(1)} lbs
                  </div>
                  <div style={{ fontSize:'0.8rem', color:'var(--on-surface)', marginTop:'0.25rem' }}>{d.flight}</div>
                  <a href={d.infosheet} download onClick={e => e.stopPropagation()} style={{ display:'flex', alignItems:'center', gap:'0.375rem', marginTop:'0.875rem', fontSize:'0.75rem', fontWeight:700, color:'var(--teal)', textDecoration:'none', borderTop:'1px solid var(--surface-high)', paddingTop:'0.75rem' }}>
                    ⬇ {t('capex.downloadInfosheet')}
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* STEP 02 */}
          <div className="card">
            <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--teal)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>STEP 02</div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
              <h2 style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.25rem', color:'var(--primary)' }}>{t('capex.step2.title')}</h2>
              <span style={{ fontSize:'0.75rem', color:'var(--teal)', fontWeight:600 }}>{t('capex.step2.included')}</span>
            </div>
            <p style={{ fontSize:'0.85rem', color:'var(--on-surface-muted)', marginBottom:'1.25rem' }}>{t('capex.step2.desc')}</p>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--surface-low)', borderRadius:'var(--radius)', padding:'1rem 1.25rem' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:'0.95rem' }}>{t('capex.step2.additionalSets')}</div>
                <div style={{ fontSize:'0.75rem', color:'var(--on-surface-muted)', marginTop:'0.2rem' }}>
                  {t('capex.step2.total')}: <strong style={{ color:'var(--primary)' }}>{numBattery + 1} set</strong> (1 {t('capex.step2.includedShort')} + {numBattery} {t('capex.step2.extra')})
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                <button onClick={() => { const v=Math.max(0,numBattery-1); setNumBattery(v); recalculate({numBattery:v}); }} style={{ width:36, height:36, borderRadius:'50%', border:'2px solid var(--surface-high)', background:'white', fontSize:'1.25rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'var(--primary)' }}>−</button>
                <span style={{ fontFamily:'var(--font-head)', fontSize:'1.75rem', fontWeight:800, color:'var(--primary)', minWidth:32, textAlign:'center' }}>{numBattery}</span>
                <button onClick={() => { const v=numBattery+1; setNumBattery(v); recalculate({numBattery:v}); }} style={{ width:36, height:36, borderRadius:'50%', border:'2px solid var(--primary)', background:'var(--primary)', fontSize:'1.25rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'white' }}>+</button>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--surface-low)', borderRadius:'var(--radius)', padding:'1rem 1.25rem', marginTop:'0.75rem' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:'0.95rem' }}>{t('capex.step2.extraChargers')}</div>
                <div style={{ fontSize:'0.75rem', color:'var(--on-surface-muted)', marginTop:'0.2rem' }}>
                  {t('capex.step2.extraChargersPrice')}
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                <button onClick={() => { const v=Math.max(0,numChargers-1); setNumChargers(v); recalculate({numChargers:v}); }} style={{ width:36, height:36, borderRadius:'50%', border:'2px solid var(--surface-high)', background:'white', fontSize:'1.25rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'var(--primary)' }}>−</button>
                <span style={{ fontFamily:'var(--font-head)', fontSize:'1.75rem', fontWeight:800, color:'var(--primary)', minWidth:32, textAlign:'center' }}>{numChargers}</span>
                <button onClick={() => { const v=numChargers+1; setNumChargers(v); recalculate({numChargers:v}); }} style={{ width:36, height:36, borderRadius:'50%', border:'2px solid var(--primary)', background:'var(--primary)', fontSize:'1.25rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'white' }}>+</button>
              </div>
            </div>
          </div>

          {/* STEP 03 — FB3 Packages + Feature Table */}
          {model === 'FB3' && (
            <div className="card">
              <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--teal)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>STEP 03</div>
              <h2 style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.25rem', color:'var(--primary)', marginBottom:'1rem' }}>{t('capex.step3.packageTitle')}</h2>
              <div style={{ display:'grid', gridTemplateColumns:isMobile ? '1fr' : '1fr 1fr 1fr', gap:'0.75rem', marginBottom:'1rem' }}>
                {fb3Packages.map(p => (
                  <div key={p.key} onClick={() => {
                    setFb3Package(p.key);
                    const map = {
                      fb3_base: { controllerType:'handheld', authorizationSupportLevel:'vlos', mmsPackage:'mms_3m', supportWarrantyPackage:'standard_12m' },
                      fb3_pro: { controllerType:'handheld_pro', authorizationSupportLevel:'bvlos', mmsPackage:'mms_12m', supportWarrantyPackage:'premium_24m' },
                      fb3_max_ultra: { controllerType:'bvlos', authorizationSupportLevel:'bvlos', mmsPackage:'mms_24m', supportWarrantyPackage:'premium_24m' }
                    }[p.key];
                    setControllerType(map.controllerType);
                    setAuthorizationSupportLevel(map.authorizationSupportLevel);
                    setMmsPackage(map.mmsPackage);
                    setSupportWarrantyPackage(map.supportWarrantyPackage);
                    recalculate({ fb3Package:p.key, ...map });
                  }}
                    style={{ border:`2px solid ${fb3Package===p.key?'var(--primary)':'var(--surface-high)'}`, borderRadius:'var(--radius)', padding:'0.9rem', cursor:'pointer', background:fb3Package===p.key?'rgba(0,58,67,0.04)':'white' }}>
                    <div style={{ fontWeight:800, color:'var(--primary)', fontSize:'0.9rem' }}>{p.label}</div>
                    <div style={{ fontSize:'0.8rem', fontWeight:700, margin:'0.25rem 0' }}>{p.price}</div>
                    <div style={{ fontSize:'0.72rem', color:'var(--on-surface-muted)' }}>{p.sub}</div>
                  </div>
                ))}
              </div>

              <h3 style={{ fontFamily:'var(--font-head)', fontWeight:700, color:'var(--primary)', marginBottom:'0.75rem' }}>{t('capex.step3.featureConfig')}</h3>
              <div style={{ display:'grid', gridTemplateColumns:isMobile ? '1fr' : '1fr 1fr', gap:'0.75rem' }}>
                <div>
                  <label className="label">{t('capex.step3.authorizationSupport')}</label>
                  <select className="input" value={authorizationSupportLevel} onChange={e => { const v=e.target.value; setAuthorizationSupportLevel(v); recalculate({ authorizationSupportLevel:v }); }}>
                    <option value="vlos">VLOS ops</option>
                    <option value="bvlos">BVLOS ops (+€2.000)</option>
                  </select>
                </div>
                <div>
                  <label className="label">{t('capex.step3.mms4g')}</label>
                  <select className="input" value={mmsPackage} onChange={e => { const v=e.target.value; setMmsPackage(v); recalculate({ mmsPackage:v }); }}>
                    <option value="mms_3m">3 months included</option>
                    <option value="mms_12m">12 months (+€1.200)</option>
                    <option value="mms_24m">24 months (+€2.400)</option>
                  </select>
                </div>
                <div>
                  <label className="label">{t('capex.step3.supportWarranty')}</label>
                  <select className="input" value={supportWarrantyPackage} onChange={e => { const v=e.target.value; setSupportWarrantyPackage(v); recalculate({ supportWarrantyPackage:v }); }}>
                    <option value="standard_12m">Standard support + 12m warranty</option>
                    <option value="premium_24m">Premium support + 24m (+€10.000)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Ground Station Selection (manual override always available) */}
          <div className="card">
            <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--teal)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>
              {model === 'FB3' ? 'STEP 04' : 'STEP 03'}
            </div>
            <h2 style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.25rem', color:'var(--primary)', marginBottom:'1rem' }}>
              {t('capex.step3.title')}
            </h2>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
              {controllers.map(c => {
                const delta = (controllerPrices[c.val] || 0) - (controllerPrices[packageDefaultController] || 0);
                return (
                  <div key={c.val} onClick={() => { setControllerType(c.val); recalculate({ controllerType: c.val }); }}
                    style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1rem 1.25rem', borderRadius:'var(--radius)', border:`2px solid ${controllerType===c.val?'var(--primary)':'var(--surface-high)'}`, background:controllerType===c.val?'rgba(0,58,67,0.04)':'white', cursor:'pointer', transition:'all 0.15s' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                      <div style={{ width:20, height:20, borderRadius:'50%', border:`2px solid ${controllerType===c.val?'var(--primary)':'var(--surface-high)'}`, background:controllerType===c.val?'var(--primary)':'white', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {controllerType===c.val && <div style={{ width:8, height:8, borderRadius:'50%', background:'white' }} />}
                      </div>
                      <div>
                        <div style={{ fontWeight:700, fontSize:'0.9rem' }}>
                          {c.label}
                          {c.val === packageDefaultController && (
                            <span style={{ marginLeft:'0.5rem', fontSize:'0.65rem', fontWeight:700, background:'var(--teal)', color:'white', borderRadius:4, padding:'0.1rem 0.4rem' }}>
                              {t('capex.step3.defaultForPackage')}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize:'0.75rem', color:'var(--on-surface-muted)', marginTop:'0.2rem' }}>{c.sub}</div>
                      </div>
                    </div>
                    <span style={{ fontWeight:700, color:'var(--primary)', fontSize:'0.9rem', whiteSpace:'nowrap', marginLeft:'1rem' }}>
                      {delta === 0 ? t('capex.included') : `${delta > 0 ? '+' : ''}${fmt(delta)}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* STEP 04 — Accessori */}
          <div className="card">
            <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--teal)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>{model==='FB3'?'STEP 05':'STEP 04'}</div>
            <h2 style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.25rem', color:'var(--primary)', marginBottom:'1.25rem' }}>{t('capex.step4.title')}</h2>
            {accessories.map(a => (
              <div key={a.key} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1rem 1.25rem', borderRadius:'var(--radius)', background:a.state?'var(--surface-low)':'white', marginBottom:'0.5rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                  <a.icon size={18} color="var(--teal)" />
                  <div>
                    <div style={{ fontWeight:600, fontSize:'0.9rem' }}>{a.label}</div>
                    <div style={{ fontSize:'0.75rem', color:'var(--on-surface-muted)' }}>{a.sub}</div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                  <span style={{ fontWeight:700, color:'var(--primary)', fontSize:'0.9rem' }}>{a.price}</span>
                  <button onClick={a.toggle} style={{ fontSize:'0.75rem', fontWeight:700, padding:'0.375rem 0.875rem', borderRadius:6, border:`1px solid ${a.state?'var(--primary)':'var(--surface-high)'}`, background:a.state?'var(--primary)':'white', color:a.state?'white':'var(--on-surface)', cursor:'pointer' }}>
                    {a.state ? `✕ ${t('capex.remove')}` : t('capex.add')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* STEP 05 — Training */}
          <div className="card">
            <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--teal)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.5rem' }}>{model==='FB3'?'STEP 06':'STEP 05'}</div>
            <h2 style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.25rem', color:'var(--primary)', marginBottom:'0.375rem' }}>{t('capex.step5.title')}</h2>
            <p style={{ fontSize:'0.85rem', color:'var(--on-surface-muted)', marginBottom:'1.25rem' }}>{t('capex.step5.desc')}</p>
            <div style={{ display:'grid', gridTemplateColumns:isMobile ? '1fr' : '1fr 1fr', gap:'0.75rem' }}>
              {trainings.map(tr => (
                <div key={tr.key} onClick={() => { const v=!tr.state; tr.set(v); recalculate({ [tr.field]:v }); }}
                  style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'1rem', borderRadius:'var(--radius)', border:`2px solid ${tr.state?'var(--primary)':'var(--surface-high)'}`, background:tr.state?'rgba(0,58,67,0.04)':'white', cursor:'pointer', transition:'all 0.15s' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:'0.85rem', marginBottom:'0.2rem' }}>{tr.label}</div>
                    <div style={{ fontSize:'0.72rem', color:'var(--on-surface-muted)' }}>{tr.sub}</div>
                    <div style={{ fontWeight:700, color:'var(--primary)', fontSize:'0.85rem', marginTop:'0.5rem' }}>{tr.price}</div>
                  </div>
                  <div style={{ width:20, height:20, borderRadius:4, border:`2px solid ${tr.state?'var(--primary)':'var(--surface-high)'}`, background:tr.state?'var(--primary)':'white', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginLeft:'0.75rem', marginTop:'0.1rem' }}>
                    {tr.state && <Check size={12} color="white" />}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Pannello destra */}
        <div style={{ position:isMobile ? 'static' : 'sticky', top:'1.5rem', display:'flex', flexDirection:'column', gap:'1rem' }}>
          <div className="card">
            <div style={{ fontSize:'0.7rem', color:'var(--on-surface-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.25rem' }}>{t('capex.quote.label')}</div>
            <div style={{ fontFamily:'var(--font-head)', fontSize:'2rem', fontWeight:800, color:'var(--primary)' }}>
              {capexResult ? fmt(capexResult.totalCapex) : '—'}
            </div>
            <div style={{ fontSize:'0.8rem', color:'var(--on-surface-muted)', marginBottom:'1rem' }}>{t('capex.quote.full')}</div>

            {capexResult?.packageLabel && (
              <div style={{ fontSize:'0.8rem', fontWeight:700, color:'var(--teal)', marginBottom:'0.75rem' }}>
                {capexResult.packageLabel}
              </div>
            )}

            {capexResult?.includedItems?.length > 0 && (
              <div style={{ marginBottom:'1rem' }}>
                <div style={{ fontSize:'0.72rem', color:'var(--on-surface-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.5rem' }}>
                  {t('capex.quote.includedInPrice')}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem' }}>
                  {capexResult.includedItems.map((item) => (
                    <div key={item} style={{ fontSize:'0.78rem', color:'var(--on-surface)', lineHeight:1.3 }}>
                      • {item}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {capexResult?.selectedExtras?.length > 0 && (
              <div style={{ marginBottom:'1rem' }}>
                <div style={{ fontSize:'0.72rem', color:'var(--on-surface-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.5rem' }}>
                  Extra Selezionati
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem' }}>
                  {capexResult.selectedExtras.map((item) => (
                    <div key={item} style={{ fontSize:'0.78rem', color:'var(--on-surface)', lineHeight:1.3 }}>
                      • {item}
                    </div>
                  ))}
                </div>
              </div>
            )}
    
            {!capexResult && (
              <button className="btn-secondary" style={{ width:'100%', justifyContent:'center' }} onClick={() => recalculate()}>
                {t('capex.quote.calculate')}
              </button>
            )}
          </div>
          {capexResult && (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
              {!viewMode && (
                <button className="btn-primary" style={{ justifyContent:'center', padding:'1rem' }} onClick={handleSave} disabled={saving}>
                  {saving ? t('capex.quote.saving') : t('capex.quote.save')} <ChevronRight size={16} />
                </button>
              )}
              {viewMode && (
                <button className="btn-primary" style={{ justifyContent:'center', padding:'1rem' }} onClick={() => setViewMode(false)}>
                  ✏️ {t('capex.quote.edit')}
                </button>
              )}
              <button className="btn-secondary" style={{ justifyContent:'center' }}>PDF Quote</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
